import React, { createContext, useEffect, useState } from 'react'
import { IChaseItemType } from '../types/chase-item-types';
import { IChaseData } from '../types/chase-types';
import axiosInstance from '../utils/axiosInstance';
import ChaseExpiredCard from '../components/chase-expired-card';
import ChaseNotFound from '../components/chase-not-found';
import MainLayout from '../layouts/MainLayout';
import { IFilesObject } from '../types/files-types';
import ChaseLoader from '../components/loader-chase';

type TErrorTypes = "no-chase" | "chase-expired"

export type IChaseLogicContext = {
    chase: IChaseData | undefined
    chaseType: IChaseItemType | null
    onSetChaseType: (chasetype?: IChaseItemType) => void;
    onSetDriverId: (driverID?: number) => void;
    driverId: number | null;
    onPushFiles: (file: IFilesObject) => void;
    onUpdateFiles: (file: IFilesObject) => void;
    onDeleteFile: (fileKey: string) => void;
    ToggleMessage: () => void;
    isMessage: boolean;
    isUploading: boolean;
    setIsUploading: (val: boolean) => void;
    newFiles: IFilesObject[]
};

export const ChaseLogicContext = createContext<IChaseLogicContext | undefined>(undefined)

const ChaseLogicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isMessage, setMessage] = useState<boolean>(false)
    const [chase, setChase] = useState<IChaseData | undefined>(undefined)
    const [chaseType, setChaseType] = useState<IChaseItemType | null>(null)
    const [driverId, setDriverId] = useState<number | null>(null)
    const refrence_number = window.location.pathname.split("/").pop()
    const [error, setError] = useState<TErrorTypes | undefined>()
    const [newFiles, setNewFiles] = useState<IFilesObject[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [isUploading, setIsUploading] = useState<boolean>(false)


    const ToggleMessage = () => {
        setMessage(!isMessage)
    }

    // -------------------------------------------------------------------------

    const onPushFiles = (file: IFilesObject) => {
        setNewFiles((prev) => ([...prev, file]))
    }

    const onDeleteFile = (fileKey: string) => {
        setNewFiles((prev) => prev.filter((prevFile) => prevFile.fileLink !== fileKey))
    }

    const onUpdateFiles = (file: IFilesObject) => {
        setNewFiles((prev) => prev.map((newfile) => {
            if (newfile.name === file.name) {
                return file
            } else {
                return newfile
            }
        }))
    }

    // ----------------------- [ FIND CHASE DATA ] ----------------------------------
    useEffect(() => {
        const findChaseData = async () => {
            setLoading(true)
            try {
                const res = await axiosInstance.get(`/chase/public/${refrence_number}`)
                setChase(res.data.data)
                setLoading(false)
            } catch (error: any) {
                const errorResponse = error?.response?.status
                setError(errorResponse === 404 ? "no-chase" : errorResponse === 410 ? "chase-expired" : undefined)
                setLoading(false)
            }
        }

        refrence_number && findChaseData()
    }, [refrence_number, chaseType])


    // --------------------- [ SET CHASE TYPE ] --------------------------------------
    const onSetChaseType = (chasetype?: IChaseItemType) => {
        setChaseType(chasetype ? chasetype : null)
        !chaseType && setNewFiles([])
    }

    // --------------------- [ SET Driver Id ] --------------------------------------
    const onSetDriverId = (driverID?: number) => {
        setDriverId(driverID ? driverID : null)
    }

    if (loading) {
        return <MainLayout>
            <ChaseLoader />
        </MainLayout>
    }


    if (error === "chase-expired") {
        return (
            <MainLayout>
                <ChaseExpiredCard />
            </MainLayout>
        )
    }

    if (error === "no-chase" || !chase?.policyReferenceNumber) {
        return <MainLayout> <ChaseNotFound /></MainLayout>
    }


    return (
        <ChaseLogicContext.Provider value={{ isMessage, ToggleMessage, onSetChaseType, onSetDriverId, driverId, chaseType, chase, onPushFiles, onDeleteFile, onUpdateFiles, newFiles, setIsUploading, isUploading }}>
            {children}
        </ChaseLogicContext.Provider>
    )
}

export default ChaseLogicProvider
